import type {
  GanttV5InteractionAction,
  GanttV5InteractionState,
  GanttV5MoveDraft,
  GanttV5Task,
  GanttV5TaskSpan,
  GanttV5Transaction,
} from './types';

export const createGanttV5InteractionState = (
  collapsedGroupIds: ReadonlySet<string> = new Set<string>(),
): GanttV5InteractionState => ({
  collapsedGroupIds: new Set(collapsedGroupIds),
  selectedTaskIds: new Set<string>(),
  focusedTaskId: null,
  hoveredTaskId: null,
  transaction: null,
  announcement: '',
});

const updateSet = (source: ReadonlySet<string>, id: string, included: boolean): ReadonlySet<string> => {
  const result = new Set(source);
  if (included) result.add(id);
  else result.delete(id);
  return result;
};

export function ganttV5InteractionReducer(
  state: GanttV5InteractionState,
  action: GanttV5InteractionAction,
): GanttV5InteractionState {
  switch (action.type) {
    case 'SET_GROUP_COLLAPSED':
      return {
        ...state,
        collapsedGroupIds: updateSet(state.collapsedGroupIds, action.groupId, action.collapsed),
      };
    case 'SET_ALL_GROUPS_COLLAPSED': {
      const next = new Set(state.collapsedGroupIds);
      for (const groupId of action.groupIds) {
        if (action.collapsed) next.add(groupId);
        else next.delete(groupId);
      }
      return { ...state, collapsedGroupIds: next };
    }
    case 'SELECT_ONLY':
      return {
        ...state,
        selectedTaskIds: new Set([action.taskId]),
        focusedTaskId: action.taskId,
        announcement: '已选择 1 个任务',
      };
    case 'TOGGLE_SELECTED': {
      const selected = !state.selectedTaskIds.has(action.taskId);
      const next = updateSet(state.selectedTaskIds, action.taskId, selected);
      return {
        ...state,
        selectedTaskIds: next,
        focusedTaskId: action.taskId,
        announcement: `已选择 ${next.size} 个任务`,
      };
    }
    case 'SET_SELECTED':
      return {
        ...state,
        selectedTaskIds: new Set(action.taskIds),
        focusedTaskId: action.taskIds.length > 0 ? action.taskIds[action.taskIds.length - 1] : null,
        announcement: `已选择 ${action.taskIds.length} 个任务`,
      };
    case 'CLEAR_SELECTED':
      return { ...state, selectedTaskIds: new Set(), announcement: '已清除任务选择' };
    case 'FOCUS_TASK':
      return { ...state, focusedTaskId: action.taskId };
    case 'HOVER_TASK':
      return { ...state, hoveredTaskId: action.taskId };
    case 'BEGIN_TRANSACTION':
      return {
        ...state,
        transaction: action.transaction,
        announcement: `正在保存 ${action.transaction.updates.length} 个任务的时间调整`,
      };
    case 'COMMIT_TRANSACTION':
      if (state.transaction?.id !== action.id) return state;
      return {
        ...state,
        transaction: { ...state.transaction, status: 'committed', error: undefined },
        announcement: '时间调整已保存',
      };
    case 'FAIL_TRANSACTION':
      if (state.transaction?.id !== action.id) return state;
      return {
        ...state,
        transaction: { ...state.transaction, status: 'failed', error: action.error },
        announcement: `时间调整保存失败：${action.error}`,
      };
    case 'CLEAR_TRANSACTION':
      if (state.transaction?.id !== action.id) return state;
      return { ...state, transaction: null };
    case 'ANNOUNCE':
      return { ...state, announcement: action.message };
    default:
      return state;
  }
}

const snap = (value: number, interval: number): number => (
  interval > 0 ? Math.round(value / interval) * interval : value
);

export interface CreateGanttV5MoveDraftInput {
  tasks: Map<string, GanttV5Task>;
  taskIds: string[];
  deltaHours: number;
  snapHours?: number;
}

export function createGanttV5MoveDraft({
  tasks,
  taskIds,
  deltaHours,
  snapHours = 0.25,
}: CreateGanttV5MoveDraftInput): GanttV5MoveDraft {
  const snappedDelta = snap(deltaHours, snapHours);
  const originals: GanttV5TaskSpan[] = [];
  const updates: GanttV5TaskSpan[] = [];
  const violations: GanttV5MoveDraft['violations'] = [];

  for (const taskId of taskIds) {
    const task = tasks.get(taskId);
    if (!task) continue;
    originals.push({ taskId, start: task.start, end: task.end });
    if (task.readOnly || task.draggable === false) {
      violations.push({ taskId, reason: 'READ_ONLY' });
      continue;
    }
    const duration = task.end - task.start;
    const nextStart = task.start + snappedDelta;
    const nextEnd = nextStart + duration;
    updates.push({ taskId, start: nextStart, end: nextEnd });
  }

  return { originals, updates, deltaHours: snappedDelta, violations };
}

export function applyGanttV5TaskSpans(
  tasks: GanttV5Task[],
  spans: GanttV5TaskSpan[],
): GanttV5Task[] {
  const spanByTaskId = new Map(spans.map((span) => [span.taskId, span] as const));
  return tasks.map((task) => {
    const span = spanByTaskId.get(task.id);
    return span ? { ...task, start: span.start, end: span.end } : task;
  });
}

let transactionSequence = 0;

export function createGanttV5Transaction(
  kind: GanttV5Transaction['kind'],
  draft: Pick<GanttV5MoveDraft, 'originals' | 'updates'>,
): GanttV5Transaction {
  transactionSequence += 1;
  return {
    id: `gantt-v5-tx-${Date.now()}-${transactionSequence}`,
    kind,
    status: 'pending',
    originals: draft.originals,
    updates: draft.updates,
  };
}
